import { Group, Stack, Text } from '@mantine/core'
import { Checkbox } from '../Common/Checkbox'
import { NumberInput } from '../Common/NumberInput'
import { Select } from '../Common/Select'
import { Textarea } from '../Common/Textarea'
import { TextInput } from '../Common/TextInput'
import type { TimeEntryFormValues } from '../../types/timeEntry'

// Enforced by the textarea itself, which also truncates a longer paste.
const DESCRIPTION_MAX = 1000

// A dropdown and the state of whatever fetch fills it.
export interface TimeEntrySelectField {
  options: { value: string; label: string }[]
  loading?: boolean
  // Why the dropdown can't be used yet — e.g. Labor Code before a work package is chosen.
  disabled?: boolean
  placeholder?: string
  // Marks the field required — the backend rejects a time entry without it.
  required?: boolean
  // Load or validation failure, shown under the dropdown.
  error?: string
}

export interface TimeEntryFormErrors {
  timeWorked?: string
  date?: string
  laborCode?: string
  billingRole?: string
  description?: string
}

export interface TimeEntryFormProps {
  values: TimeEntryFormValues
  workPackage: TimeEntrySelectField
  laborCode: TimeEntrySelectField
  billingRole: TimeEntrySelectField
  billingStatus: TimeEntrySelectField
  // Latest date that can be booked, yyyy-mm-dd — time is never entered ahead of the day
  // it was worked.
  maxDate: string
  // Earliest date that can be booked, yyyy-mm-dd.
  minDate: string
  // Backend feature flag (EnableFileNote) — when off, the "Create File Note" checkbox is
  // absent, so a time entry has no way to file a note.
  showFileNote: boolean
  onChange: <K extends keyof TimeEntryFormValues>(field: K, value: TimeEntryFormValues[K]) => void
  errors?: TimeEntryFormErrors
}

// Mantine renders its error text with no role, so an error that appears while the user is
// typing is never announced. Wrapping it makes each one an alert; the wrapper still sits
// inside the element Mantine points aria-describedby at, and still sets aria-invalid.
function announce(message?: string) {
  return message ? <span role="alert">{message}</span> : undefined
}

interface LookupSelectProps {
  label: string
  field: TimeEntrySelectField
  value: string
  onChange: (value: string) => void
}

// Mantine only clears the text a Select shows when its value is null — an empty string
// leaves the previous label on screen, so a field cleared in code would still look set.
function LookupSelect({ label, field, value, onChange }: Readonly<LookupSelectProps>) {
  return (
    <Select
      size="xs"
      label={label}
      withAsterisk={field.required}
      data={field.options}
      value={value === '' ? null : value}
      placeholder={field.loading ? 'Loading…' : field.placeholder}
      disabled={field.disabled || field.loading}
      error={announce(field.error)}
      onChange={(next) => onChange(next ?? '')}
    />
  )
}

export function TimeEntryForm({
  values,
  workPackage,
  laborCode,
  billingRole,
  billingStatus,
  maxDate,
  minDate,
  showFileNote,
  onChange,
  errors,
}: Readonly<TimeEntryFormProps>) {
  return (
    <Stack gap="sm">
      <Group gap="xs" align="flex-start" grow wrap="nowrap">
        <NumberInput
          withAsterisk
          size="xs"
          label="Hours (e.g., 1.5)"
          description="1.5 = 1 hr 30 min"
          // The hint sits under the input, not above it, so this column's label and input stay
          // on the same lines as Date Worked's instead of being pushed down a row.
          inputWrapperOrder={['label', 'input', 'description', 'error']}
          placeholder="1.5"
          value={values.timeWorked}
          onChange={(value) => onChange('timeWorked', value)}
          min={0}
          // Hours are entered as a decimal, to the nearest 0.01 (0.6 of a minute).
          decimalScale={2}
          step={0.25}
          error={announce(errors?.timeWorked)}
        />
        <TextInput
          withAsterisk
          size="xs"
          label="Date Worked"
          type="date"
          min={minDate}
          max={maxDate}
          value={values.date}
          onChange={(event) => onChange('date', event.currentTarget.value)}
          error={announce(errors?.date)}
        />
      </Group>

      <Group gap="xs" align="flex-start" grow wrap="nowrap">
        <LookupSelect
          label="Work Package"
          field={workPackage}
          value={values.workPackage}
          onChange={(value) => onChange('workPackage', value)}
        />
        <LookupSelect
          label="Labor Code"
          field={laborCode}
          value={values.laborCode}
          onChange={(value) => onChange('laborCode', value)}
        />
        <LookupSelect
          label="Billing Role"
          field={billingRole}
          value={values.billingRole}
          onChange={(value) => onChange('billingRole', value)}
        />
      </Group>

      <LookupSelect
        label="Billing Status"
        field={billingStatus}
        value={values.billingStatus}
        onChange={(value) => onChange('billingStatus', value)}
      />

      <Stack gap={4}>
        <Textarea
          withAsterisk
          size="xs"
          label="Description"
          placeholder="e.g. Reviewed the loss adjuster report and called the insured to confirm scope"
          value={values.description}
          onChange={(event) => onChange('description', event.currentTarget.value)}
          error={announce(errors?.description)}
          maxLength={DESCRIPTION_MAX}
          autosize
          minRows={5}
          // Grows with the text, then scrolls — a long note must not push Save off screen.
          maxRows={10}
        />
        {/* Not a live region: it changes on every keystroke, which would drown out the field. */}
        <Text fz="xs" c="text.2" ta="right">
          {values.description.length}/{DESCRIPTION_MAX}
        </Text>
      </Stack>

      {showFileNote && (
        <Checkbox
          size="xs"
          label="Create File Note"
          checked={values.createFileNote}
          onChange={(event) => onChange('createFileNote', event.currentTarget.checked)}
        />
      )}
    </Stack>
  )
}
